import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import {
  CLIENT_REPORT_CATEGORIES,
  GAP_GUIDANCE,
  LAND_SYSTEMS,
  OPTIONAL_NOTIFICATION_CHANNELS,
  type ClientReportCategory,
  type ConsentChannel,
  type IntakeGap,
  type LandSystem,
} from "@titleverify/shared";
import {
  apiErrorText,
  apiFetch,
  type Case,
  type IntakeSubmission,
  type MyClientReport,
} from "../api.js";
import { useAuth } from "../auth.js";
import { formatDate } from "./caseDisplay.js";
import { CaseContactChannels } from "../components/CaseContactChannels.js";
import { CaseOpinion } from "../components/CaseOpinion.js";
import { ParcelIdentity } from "../components/ParcelIdentity.js";
import { SUPPORT_EMAIL } from "../support.js";
import { CaseDocuments } from "../components/CaseDocuments.js";
import { CaseProgress } from "../components/CaseProgress.js";
import { ConsentWithdrawal } from "../components/ConsentWithdrawal.js";
import { LoadFailure } from "../components/LoadFailure.js";
import { SupplementCase } from "../components/SupplementCase.js";
import { ADVISORY_PROMPT, CHANNEL_CONSENT, LAND_SYSTEM_LABEL } from "../intakeCopy.js";

/**
 * The client's own surface: their cases, and the way to tell a human something
 * went wrong.
 *
 * WHAT IS DELIBERATELY ABSENT. There is no list of professionals, no way to
 * browse or compare them, and no name of anybody the client is not already
 * working with. A client sees the professional on their OWN case and nobody
 * else's — a client-facing surface organised around named attorneys is a
 * directory, and a directory that attracts work for named professionals is what
 * COMPLIANCE Constraint 1 forbids.
 *
 * Nothing on this page states a fact about the client's land. Status is the
 * platform's own workflow state; where a registry district has not been resolved
 * it says so rather than leaving a blank that reads like an answer (CLAUDE.md
 * non-negotiable 1).
 */
export function ClientCases() {
  const { getToken } = useAuth();

  const cases = useQuery({
    queryKey: ["cases", "mine"],
    queryFn: async () => apiFetch<{ cases: Case[] }>("/cases", { token: await getToken() }),
  });

  const reports = useQuery({
    queryKey: ["reports", "mine"],
    queryFn: async () =>
      apiFetch<{ reports: MyClientReport[] }>("/reports/mine", { token: await getToken() }),
  });

  return (
    <section>
      <NewRequest />

      <h2>Your verification requests</h2>

      {cases.isPending ? <p className="muted" role="status">Loading your cases…</p> : null}
      {cases.error ? (
        <LoadFailure error={cases.error} fallback="Could not load your requests.">
          <p>
            <small>Your requests are unaffected — it is the list that did not load.</small>
          </p>
        </LoadFailure>
      ) : null}

      {cases.data ? (
        cases.data.cases.length === 0 ? (
          /*
            AN EMPTY LIST, WITH THE WAY IN RATHER THAN JUST THE FACT.
            "You have not opened a verification request yet" is true and was all
            this said. A person who has just arrived, is anxious about their
            land, and does not yet know whether they hold enough paperwork to
            even begin, reads a bare absence as a closed door. So the fact stays
            first — it is the honest headline — and what to do next follows it.
          */
          <p className="empty">
            <strong>No requests yet.</strong> Start one above — a part-filled form is fine.
          </p>
        ) : (
          <ul className="case-list">
            {cases.data.cases.map((c) => (
              <CaseCard key={c.id} caseRow={c} />
            ))}
          </ul>
        )
      ) : null}

      <h2>Problems you have reported</h2>
      {reports.isPending ? <p className="muted" role="status">Loading…</p> : null}
      {reports.error ? (
        <LoadFailure
          error={reports.error}
          fallback="Could not load the problems you have reported."
        >
          <p>
            <small>Your reports are still recorded — it is the list that did not load.</small>
          </p>
        </LoadFailure>
      ) : null}
      {reports.data ? (
        reports.data.reports.length === 0 ? (
          /*
            THIS SENTENCE USED TO ASSERT A CHANNEL THAT MOSTLY DOES NOT EXIST.
            It read "No problems reported. Each request above has a way to tell
            us." — printed on every client's empty reports list, including the
            common case where it is false. `POST /reports` resolves the subject
            of a report from the case's ASSIGNED PROFESSIONAL and hard-refuses
            when there is none, so a request at SUBMITTED, VALIDATING,
            NEEDS_INFO or ROUTED has no way to tell us anything. That is every
            case before assignment, which is where cases spend most of their
            life.

            So it now says only what is true, and names the one thing the reader
            can actually do — which depends on whether an inbox has an owner yet
            (see support.ts). Both branches are honest; neither invents a
            channel.
          */
          <p className="empty">
            No problems reported. Once an attorney is assigned to a request, that request
            gets a way to report a problem with it.
            {SUPPORT_EMAIL ? (
              <>
                {" "}
                Before then, or for anything else, write to{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </>
            ) : null}
          </p>
        ) : (
          <ul className="report-list">
            {reports.data.reports.map((r) => (
              <li key={r.id} className="report-card">
                <dl>
                  <dt>Reported</dt>
                  <dd>{new Date(r.createdAt).toLocaleDateString()}</dd>
                  <dt>About case</dt>
                  <dd>
                    <code>{r.caseId}</code>
                  </dd>
                  <dt>What you told us</dt>
                  <dd>{CATEGORY_LABEL[r.category]}</dd>
                  {r.detail ? (
                    <>
                      <dt>In your words</dt>
                      {/* Marked as theirs. The platform has not adopted any of it. */}
                      <dd>
                        <blockquote>{r.detail}</blockquote>
                      </dd>
                    </>
                  ) : null}
                  <dt>Where it has got to</dt>
                  {/*
                    The server's wording, verbatim, from
                    CLIENT_REPORT_STATUS_MEANINGS. Never re-derived here: the
                    word "upheld" means something narrow and specific in this
                    system, and a client must not read it as us having found
                    their attorney at fault — which is not ours to find.
                  */}
                  <dd>{r.statusMeaning}</dd>
                </dl>
              </li>
            ))}
          </ul>
        )
      ) : null}

      <AccountConsent />
    </section>
  );
}

/**
 * WITHDRAWING A CHANNEL EVERYWHERE, kept visibly apart from the per-case control.
 *
 * Two separate controls rather than a scope dropdown, because the two requests
 * are genuinely different and the API refuses to guess between them — `scope`
 * is required with no default for exactly this reason. "Stop WhatsApping me
 * about this one" and "stop WhatsApping me entirely" collapse badly in both
 * directions: guessing narrow under-revokes, which is a compliance failure, and
 * guessing wide cuts a client off from cases they still wanted to hear about.
 *
 * A dropdown would have made the wide option one mis-click from the narrow one,
 * under a single button whose label could only describe one of them. So this
 * sits at the foot of the page, opens deliberately, and says what it covers
 * before it can be pressed.
 *
 * It is behind a disclosure and NOT rendered open: an irreversible act that
 * spans every case a client has should be arrived at on purpose, not scrolled
 * into.
 */
function AccountConsent() {
  const [open, setOpen] = useState(false);

  return (
    <section className="stack">
      <h2>Contact permissions across all your requests</h2>
      <p>
        You gave these one request at a time, and the usual place to change your mind is on
        the request itself. This is the wider version, for when you want a channel stopped
        everywhere at once.
      </p>
      <p>
        <small>
          <Link href="/data">
            What TitleVerify holds about you, who can see it, and how long it is kept
          </Link>
        </small>
      </p>

      <div className="actions">
        <button type="button" className="quiet" onClick={() => setOpen((o) => !o)}>
          {open ? "Close" : "Withdraw a channel across every request"}
        </button>
      </div>

      {open ? <ConsentWithdrawal scope={{ allCases: true }} /> : null}
    </section>
  );
}

const CATEGORY_LABEL: Record<ClientReportCategory, string> = {
  WORK_NOT_COMPLETED: "The work I asked for has not been delivered",
  UNRESPONSIVE: "I have not been able to get a reply",
  MISSED_APPOINTMENT: "An arranged meeting did not happen",
  FEE_DISPUTE: "I disagree with what I was asked to pay",
  CONDUCT_CONCERN: "I have a concern about how I was treated",
  OTHER: "Something else",
};

/**
 * Step one of the whole product: the client says which land they want checked.
 *
 * THE FORM COLLECTS; IT DOES NOT JUDGE. Every field below is optional at the API
 * on purpose (see the comment above `parcelSchema`) — the form's job is to take
 * what the client HAS, and the sufficiency rule in the shared package decides
 * whether that is enough to find the parcel at a registry. So there is no
 * client-side "this looks sufficient" anywhere here, and no required attribute
 * standing in for one. A browser verdict that disagreed with the server's would be
 * exactly the drift that keeping the rule in one attorney-readable file prevents.
 *
 * The hints under each group are GAP_GUIDANCE strings, rendered rather than
 * paraphrased. If the attorney corrects what a searcher actually needs, this form
 * asks for the corrected thing without anyone remembering to edit it here.
 */
function NewRequest() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_PARCEL);
  const [channels, setChannels] = useState<ConsentChannel[]>([]);
  const [outcome, setOutcome] = useState<IntakeSubmission | null>(null);

  const set = (field: keyof typeof EMPTY_PARCEL) => (value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const submit = useMutation({
    mutationFn: async () =>
      apiFetch<IntakeSubmission>("/cases", {
        token: await getToken(),
        method: "POST",
        body: {
          parcel: {
            district: orNull(form.district),
            dsDivision: orNull(form.dsDivision),
            village: orNull(form.village),
            address: orNull(form.address),
            surveyPlanNo: orNull(form.surveyPlanNo),
            lotNo: orNull(form.lotNo),
            extent: orNull(form.extent),
            currentDeedNo: orNull(form.currentDeedNo),
            currentDeedDate: orNull(form.currentDeedDate),
            currentDeedNotary: orNull(form.currentDeedNotary),
            priorDeedRefs: form.priorDeedRefs
              .split("\n")
              .map((line) => line.trim())
              .filter((line) => line.length > 0),
            // Empty means the client did not say, which is null — never a guess
            // at "deed" because most parcels are. Which system applies is the
            // attorney's finding from the registry, not this form's.
            landSystem: form.landSystem === "" ? null : form.landSystem,
          },
          notificationChannels: channels,
        },
      }),
    onSuccess: (result) => {
      setOutcome(result);
      // The case exists whichever status came back, so the list below must show
      // it — including when it came back NEEDS_INFO, which is where the client
      // goes to see the case and, if they need to, report a problem on it.
      void queryClient.invalidateQueries({ queryKey: ["cases", "mine"] });
    },
  });

  if (outcome) {
    return (
      <Submitted
        result={outcome}
        onAnother={() => {
          setOutcome(null);
          setForm(EMPTY_PARCEL);
          setChannels([]);
          submit.reset();
        }}
      />
    );
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submit.mutate();
  }

  return (
    <>
      <header className="page-head">
        <p className="eyebrow">Verification request</p>
        <h2>Open a verification request</h2>
        <p className="lede">One request, one piece of land. Send what you have.</p>
      </header>

      {/*
        SAID ONCE, BEFORE THE FIRST BOX, BECAUSE THE FORM IS LONG AND ASKS FOR
        DEED NUMBERS.
        ---------------------------------------------------------------------
        The failure mode this exists to stop is a person opening the form,
        seeing eleven fields naming documents they do not have in front of them,
        and closing it — concluding they are not ready to ask. They are; the
        sufficiency rule needs far less than the form offers.

        WHAT IT MAY AND MAY NOT SAY. It reports which fields `assessParcel`
        (packages/shared/src/intake.ts) reads as blocking and which it records as
        advisories — that is a fact about our own rule, not a fact about their
        land, and it is the same thing GAP_GUIDANCE tells them after submitting.
        Saying it BEFORE they type is the only change.

        It is emphatically NOT a client-side verdict. Nothing here decides
        whether what they typed is enough, there are no `required` attributes
        standing in for the rule, and the button is never disabled on a
        judgement about the parcel. The server assesses; this only sets
        expectations so that an honest NEEDS_INFO is not experienced as a
        rejection.
      */}
      <div className="notice">
        {/*
          THE TWO FACTS A PERSON NEEDS BEFORE THEY START, and nothing else.

          This ran to three paragraphs. Nobody reads three paragraphs above a
          form, which meant the one genuinely blocking limit — title-registered
          land — was buried in prose and effectively unsaid.

          Both lines are load-bearing and checked against `assessParcel`
          (packages/shared/src/intake.ts): it blocks on district, on parcel
          identification, AND on landSystem === "title"
          (TITLE_SYSTEM_NOT_YET_SUPPORTED, TODO-BLOCKED-TITLE-SYSTEM). "Title
          registration" is selectable in this very form, so omitting it let a
          client answer honestly and be refused by a limit they were never told
          about. Do not shorten this further by dropping the second line.
        */}
        <p>
          <strong>To start a search:</strong> the district, plus either a survey plan and lot
          number, or a deed number with its date and notary. Everything else is optional — send
          a part-filled form and we will ask for the rest.
        </p>
        <p>
          <strong>Not yet supported:</strong> land under title registration (Bim Saviya). That
          is a gap on our side. Unsure? Leave that question blank.
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <fieldset>
          <legend>Where the land is</legend>
          <p>
            <small>{GAP_GUIDANCE.DISTRICT_MISSING}</small>
          </p>

          <label htmlFor="district">District</label>
          <input
            id="district"
            type="text"
            value={form.district}
            onChange={(e) => set("district")(e.target.value)}
          />

          <label htmlFor="dsDivision">Divisional Secretariat division (if you know it)</label>
          <input
            id="dsDivision"
            type="text"
            value={form.dsDivision}
            onChange={(e) => set("dsDivision")(e.target.value)}
          />

          <label htmlFor="village">Village or town (if you know it)</label>
          <input
            id="village"
            type="text"
            value={form.village}
            onChange={(e) => set("village")(e.target.value)}
          />

          <label htmlFor="address">Address or how to find it (optional)</label>
          <textarea
            id="address"
            rows={2}
            value={form.address}
            onChange={(e) => set("address")(e.target.value)}
          />
        </fieldset>

        <fieldset>
          <legend>How the land is identified</legend>
          <p>
            <small>{GAP_GUIDANCE.PARCEL_NOT_IDENTIFIABLE}</small>
          </p>
          {/*
            THE SAME SENTENCE SupplementCase ALREADY SHOWS, MOVED EARLIER.
            The trap it exists for: five boxes read as five things a person must
            go and find, and someone holding only half of them gives up. Which
            routes exist is GAP_GUIDANCE's statement directly above, not this
            component's — the headings below only group the boxes to match it.
          */}
          <p>
            <small>
              <strong>Either group below is enough on its own.</strong> Fill in whichever one
              your papers give you and leave the other empty. If neither is complete, send what
              you have and we will ask for the rest.
            </small>
          </p>

          <label htmlFor="surveyPlanNo">Survey plan no.</label>
          <input
            id="surveyPlanNo"
            type="text"
            value={form.surveyPlanNo}
            onChange={(e) => set("surveyPlanNo")(e.target.value)}
          />

          <label htmlFor="lotNo">Lot no. on that plan</label>
          <input
            id="lotNo"
            type="text"
            value={form.lotNo}
            onChange={(e) => set("lotNo")(e.target.value)}
          />

          <p>
            <small>— or —</small>
          </p>

          <label htmlFor="currentDeedNo">Current deed no.</label>
          <input
            id="currentDeedNo"
            type="text"
            value={form.currentDeedNo}
            onChange={(e) => set("currentDeedNo")(e.target.value)}
          />

          <label htmlFor="currentDeedDate">Date of that deed</label>
          <input
            id="currentDeedDate"
            type="date"
            value={form.currentDeedDate}
            onChange={(e) => set("currentDeedDate")(e.target.value)}
          />

          <label htmlFor="currentDeedNotary">Notary who attested that deed</label>
          <input
            id="currentDeedNotary"
            type="text"
            value={form.currentDeedNotary}
            onChange={(e) => set("currentDeedNotary")(e.target.value)}
          />

          {/*
            The advisory boundary, drawn where `assessParcel` actually draws it:
            extent, prior deeds and land system are INTAKE_ADVISORIES, never
            gaps, so none of them can send a case to NEEDS_INFO. Saying so lets a
            person stop here without wondering whether the blank boxes below cost
            them something.
          */}
          <p>
            <small>
              <strong>The rest of this group is helpful rather than needed.</strong> Leaving any
              of it blank does not hold up the search or send your request back to you.
            </small>
          </p>

          <label htmlFor="extent">Extent — the size of the land (if you know it)</label>
          <input
            id="extent"
            type="text"
            value={form.extent}
            onChange={(e) => set("extent")(e.target.value)}
          />

          <label htmlFor="priorDeedRefs">Earlier deeds you know of (optional, one per line)</label>
          <textarea
            id="priorDeedRefs"
            rows={3}
            value={form.priorDeedRefs}
            onChange={(e) => set("priorDeedRefs")(e.target.value)}
          />

          <label htmlFor="landSystem">Which system the land is registered under</label>
          <select
            id="landSystem"
            value={form.landSystem}
            onChange={(e) =>
              setForm((f) => ({ ...f, landSystem: e.target.value as "" | LandSystem }))
            }
          >
            <option value="">I do not know</option>
            {LAND_SYSTEMS.map((system) => (
              <option key={system} value={system}>
                {LAND_SYSTEM_LABEL[system]}
              </option>
            ))}
          </select>
          <p>
            <small>
              Most people do not know, and that is the honest answer to give — the attorney
              establishes it from the registry. Leaving this alone does not hold anything up.
            </small>
          </p>
        </fieldset>

        <fieldset>
          <legend>How we may contact you</legend>
          {/*
            THE SENTENCE THAT WAS HERE PROMISED A DELIVERY THIS PLATFORM DOES NOT
            PERFORM: "we will always tell you here, inside this app — that is
            where the report is delivered". Neither half survived checking.

            There is no report. No entity, no route, no status past the workflow
            graph delivers an attorney's opinion to a client — `apps/api/src/reports`
            is the CLIENT PROBLEM-REPORT module (client_reports), which is a
            different thing entirely. And there is no notification of any kind:
            in_app is a consent row `recordConsents` writes, not a mechanism that
            tells anybody anything.

            The identical clause was already deleted from the landing page for
            exactly this reason. It survived here, on the one screen in the
            product where a PDPA consent is actually taken — which is the worst
            place in the product for a promise we do not keep, because it is the
            promise the consent is being given against.

            What replaces it is the true version of the same point: in_app is
            recorded unconditionally and is genuinely not offered as a choice
            (see `recordConsents`), and this page is genuinely where a client
            finds their request — but by opening it, not by being told.
          */}
          <p>
            <small>
              This app is the one place we are built to reach you, so being contacted here is
              recorded and is not offered as something to switch off. In practice that means
              your request and where it has got to are on this page whenever you open it. One
              thing is written to you: if you allow email below, we send a note when the
              attorney's opinion is ready — and nothing else, on any channel, at any point.
              Both boxes are optional and start off unticked. You can change either afterwards
              from the request itself, once it is open.
            </small>
          </p>
          {/*
            Said once, here, rather than duplicated into each CHANNEL_CONSENT
            row — see the note above that table. Verified: no mailer, no WhatsApp
            client and no dependency for either anywhere in apps/api/src, and
            `hasConsentForCase` is called by nothing but its own test.
          */}
          <p className="notice">
            Email is in use, for one message only: a note that the attorney's opinion on this
            request is ready, with a link back to this page. It carries nothing about your land.
            WhatsApp is not in use — no part of this platform can send on it, and there is
            nowhere here to give us a number — so ticking it records a permission and starts
            nothing.
          </p>

          {OPTIONAL_NOTIFICATION_CHANNELS.map((channel) => (
            <p key={channel}>
              <label>
                <input
                  type="checkbox"
                  checked={channels.includes(channel)}
                  onChange={(e) =>
                    setChannels((current) =>
                      e.target.checked
                        ? [...current, channel]
                        : current.filter((c) => c !== channel),
                    )
                  }
                />{" "}
                {CHANNEL_CONSENT[channel].label}
              </label>
              <br />
              <small>{CHANNEL_CONSENT[channel].meaning}</small>
            </p>
          ))}
        </fieldset>

        {submit.error ? (
          <div role="alert">
            {/*
              A genuine failure — the case was NOT created. Distinct from a case
              that came back needing another detail, which is a success and is
              rendered as one below.

              THROUGH apiErrorText, NOT `error.message`. The old branch printed
              `ApiError.message` raw, and on the refusals this endpoint actually
              produces that message is not a sentence: http/errors.ts strips the
              reason from every 403 and sends the bare token "forbidden", a 404
              is "not_found", and every 5xx is "internal_error". A client who
              could not open a request was shown the word "forbidden" and left to
              interpret it. apiErrorText still passes the server's OWN wording
              through untouched where the server wrote one (the 400/409
              `serverExplained` path), which is the case worth preserving here —
              it just refuses to dress a machine token up as an explanation, and
              it tells a transport failure apart from our API refusing.
            */}
            <p>{apiErrorText(submit.error, "Could not open the request.")}</p>
            {/*
              THE FEAR THIS ANSWERS IS "HAVE I JUST LOST ALL THAT TYPING".
              It is a real question after a long form fails, and the answer is
              no — `submit` failing leaves `form` and `channels` untouched, so
              every box below still holds what was typed into it. Said out loud
              because the alternative is someone scrolling up to check, or worse,
              starting again.

              It deliberately does NOT say whether the request was created. On a
              network failure or a 5xx the server may have written the case
              before the response was lost, and this page cannot tell. Claiming
              "nothing was created" would be inventing a fact about our own
              records; the list below is the honest place to find out, and it
              refetches.
            */}
            <p>
              <small>
                <strong>Everything you typed is still on this page.</strong> Nothing was
                cleared, so you can try the button again without filling any of it in a second
                time. If the request turns out to have been opened after all, it appears in
                your list below.
              </small>
            </p>
          </div>
        ) : null}

        <div className="actions">
          <button type="submit" disabled={submit.isPending}>
            {submit.isPending ? "Opening…" : "Open the request"}
          </button>
        </div>
      </form>
    </>
  );
}

const EMPTY_PARCEL = {
  district: "",
  dsDivision: "",
  village: "",
  address: "",
  surveyPlanNo: "",
  lotNo: "",
  extent: "",
  currentDeedNo: "",
  currentDeedDate: "",
  currentDeedNotary: "",
  priorDeedRefs: "",
  landSystem: "" as "" | LandSystem,
};

/** Blank means "the client did not tell us", which is null. Not an empty string that would pass a presence check while proving nothing. */
const orNull = (value: string): string | null => (value.trim() === "" ? null : value.trim());

/**
 * The result of submitting, and the part of this screen most easily got wrong.
 *
 * NEEDS_INFO IS NOT A FAILURE AND MUST NOT LOOK LIKE ONE. The case was created,
 * the parcel details were kept, and the platform is asking one more question
 * instead of sending an attorney to a registry to search for something it cannot
 * locate. So this renders in a plain panel, never `role="alert"`, and it leads
 * with the reference — the client's evidence that their work landed.
 *
 * Nothing here says anything about the land. Which registry has jurisdiction and
 * which system the parcel is under are not resolved at submission and are not in
 * this response at all, so there is nothing to report and nothing to infer
 * (CLAUDE.md non-negotiable 1).
 */
function Submitted({ result, onAnother }: { result: IntakeSubmission; onAnother: () => void }) {
  // The gap we own, separated from the gaps the client can answer. Grouping them
  // together would put "we cannot do this yet" in a list headed by things the
  // client is being asked to go and find, which reads as their failing.
  const ours = result.needsInfo.filter((gap) => gap.code === "TITLE_SYSTEM_NOT_YET_SUPPORTED");
  const theirs = result.needsInfo.filter((gap) => gap.code !== "TITLE_SYSTEM_NOT_YET_SUPPORTED");

  return (
    <>
      <header className="page-head">
        <p className="eyebrow">Verification request</p>
        <h2>Your request is open</h2>
      </header>

      {/*
        .outcome, never [role="alert"]. See the block comment above: a request
        that came back needing one more detail was still created, and the danger
        colours would tell the client the opposite of what happened.
      */}
      <div className="outcome">
        <p>
          <strong>The request was created and nothing you entered was lost.</strong> Its reference
          is <code>{result.caseId}</code>, and it is in your list below.
        </p>

        {result.needsInfo.length === 0 ? (
          <p>
            Nothing further is needed from you at this point. Which Land Registry has jurisdiction
            over the parcel, and which registration system it falls under, have not been worked
            out yet — that happens next, and this page will not guess at either.
          </p>
        ) : null}

        {theirs.length > 0 ? (
          <>
            {/*
              THE FRAMING, NOT THE FACT, IS WHAT CHANGED HERE.
              The heading was "We need one more thing before the search can
              start", and the paragraph under it opened by justifying the ask —
              a wasted registry trip, an attorney sent to look for what we cannot
              locate. All true, and all about OUR cost. A client reading it at
              the moment their request came back has just been told, in effect,
              that they nearly caused a problem.

              So the order is inverted: what is safe first, what is wanted
              second, the reasoning last for whoever wants it. Not one fact was
              removed — the wasted-trip explanation is still here, and still in
              the platform's own words.
            */}
            <h3>There is one more thing we need</h3>
            <p>
              This is the platform asking, not a fault at your end. Your request is open, and
              everything you gave us is held against it — this is the last piece before it can
              go to the registry.
            </p>
            {/*
              "you would be billed for that trip either way" was removed here. It
              asserts a billing arrangement that does not exist: there is no
              payment, invoice or fee code anywhere in apps/api/src, and WHO
              bills the client for what is precisely the open D1 question
              (CLAUDE.md §8) that no screen may pre-empt. The substantive point —
              why we ask rather than guess — stands on its own without it.
            */}
            <p>
              We would rather ask than send an attorney to a registry to look for something we
              cannot locate. A search that cannot find the parcel is a wasted trip, and it
              would tell you nothing about the land.
            </p>
            <ul>
              {theirs.map((gap) => (
                <li key={gap.code}>{guidanceFor(gap)}</li>
              ))}
            </ul>
            <p>
              Bring it to us whenever you have it and we will add it to this request. The
              request is in your list below with a place to type it in — you do not need to
              start again, and everything you have already given us is held against it.
            </p>
            {/*
              THE OTHER HALF OF FORGIVING: what to do when the answer is "I do
              not have that and I cannot get it". Without this the panel offers
              one route out, and a person who cannot take it is stranded on a
              screen that keeps asking. Deliberately does not promise what
              happens next — nothing in this platform reaches out to a client, so
              it invites them to say so and stops there rather than implying
              somebody will follow it up.
            */}
            <p>
              <small>
                If you do not have it and are not sure how to get it, tell us that rather than
                guessing at an answer — a wrong reference sends someone to a registry to look
                for land that is not there. The request keeps its place either way; there is
                nothing here that closes it for being unanswered.
              </small>
            </p>
          </>
        ) : null}

        {ours.map((gap) => (
          <div key={gap.code}>
            <h3>This one is ours, not yours</h3>
            <p>
              <strong>You have not done anything wrong and there is nothing to correct.</strong>{" "}
              We cannot take requests for title-registered land yet: the details this form asks
              for describe a deed, and a parcel registered under the Registration of Title Act is
              identified by its cadastral map and title certificate instead. Rather than accept a
              case we know we cannot search properly, we are saying so.
            </p>
            <p>{guidanceFor(gap)}</p>
          </div>
        ))}

        {result.advisories.length > 0 ? (
          <>
            <h3>Not needed, but they would help</h3>
            {/*
              "sometimes cheaper" was removed: it is a claim about what a search
              costs, and this platform has no fee model to make it from — D1 is
              open. "Faster" fared no better; nothing here measures how long a
              search takes. What each of these details actually does is narrow
              what has to be looked for, which is checkable and is what is said.
            */}
            <p>
              The search can go ahead without these. Each one narrows down what has to be
              looked for at the registry.
            </p>
            <ul>
              {result.advisories.map((code) => (
                <li key={code}>{ADVISORY_PROMPT[code]}</li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      <div className="actions">
        <button type="button" className="quiet" onClick={onAnother}>
          Open a request for another piece of land
        </button>
      </div>
    </>
  );
}

/**
 * The wording for a gap belongs to GAP_GUIDANCE in the shared package — the one
 * place an attorney can read the sufficiency rule and its copy together and
 * correct both. It is never restated here.
 *
 * The server sends the same string from the same table; the fallback covers only
 * the window where a deployed API knows a gap code this bundle does not, and it
 * exists because the alternative is a blocking requirement rendering as an empty
 * bullet the client never sees.
 */
function guidanceFor(gap: { code: IntakeGap; message: string }): string {
  return GAP_GUIDANCE[gap.code] ?? gap.message;
}

function CaseCard({ caseRow }: { caseRow: Case }) {
  const [reporting, setReporting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  return (
    <li className="case-card">
      {/*
        WHICH LAND, before the reference. A UUID is what the system calls this
        request; the parcel is what the PERSON calls it, and on a list of several
        it is the only thing that tells them apart.
      */}
      <ParcelIdentity caseRow={caseRow} viewer="client" />
      <dl>
        <dt>Reference</dt>
        <dd>
          <code>{caseRow.id}</code>
        </dd>
        <dt>Land registry</dt>
        {/* Null means not yet resolved. Never rendered as an empty cell. */}
        <dd>{caseRow.registryDistrict ?? "Not yet worked out."}</dd>
        <dt>Attorney handling it</dt>
        <dd>
          {caseRow.assignedProfessionalId
            ? "An attorney-at-law is assigned to this case."
            : "Nobody is assigned to it at the moment."}
        </dd>
        {/*
          OPENED, because elapsed time is the only signal a waiting client has
          left. The platform refuses on principle to estimate how long a step
          takes — defensible — and it does not notify, so how long a request has
          been sitting is the whole of what someone can reason from. The operator
          already gets this on their queue; the client, who has more at stake,
          had to open the detail page one case at a time to find it.

          A date something HAPPENED is a fact we hold, not an estimate of
          something that will happen, so this does not cut across the no-ETA
          rule.

          `createdAt` ONLY, deliberately. `updatedAt` moves on any write to the
          row — including ops actions the client never sees — so showing it would
          let someone watch a date advance and infer progress that did not
          happen. That is the exact class of inferred fact the rest of this
          product works to avoid.
        */}
        <dt>Opened</dt>
        <dd>{formatDate(caseRow.createdAt)}</dd>
      </dl>

      {/*
        Where the request has got to and whose move it is, in words rather than
        as a token. The status row was removed from the list above rather than
        kept alongside this: the same fact stated twice on one card is how the
        two drift when one of them is edited.
      */}
      <CaseProgress caseRow={caseRow} />

      {/*
        The thing the client came for. Renders nothing until an opinion exists —
        which is most of a case's life — and when it does, it is the attorney's
        words, not our summary of them. See CaseOpinion.
      */}
      <CaseOpinion caseRow={caseRow} />

      {/*
        Rendered on every case rather than behind a toggle, because the landing
        page tells a client they can add the papers they already hold and a
        promise made there should not have to be hunted for here. The client is
        granted every kind on their own case, so this panel shows them the whole
        of what the platform holds on it.
      */}
      <CaseDocuments caseId={caseRow.id} viewer="client" />

      {/*
        The way out of NEEDS_INFO, on the card rather than behind a link. This
        renders nothing at all on a case that is not waiting on the client, so
        it costs a settled case no space — but a stranded one must not require
        the client to go looking for the screen that unsticks it. Which fields
        appear is the server's answer, read off this row's own gaps.
      */}
      <SupplementCase caseRow={caseRow} />

      <div className="actions">
        <Link href={`/cases/${caseRow.id}`}>See everything on this case</Link>
        <button type="button" className="quiet" onClick={() => setReporting((r) => !r)}>
          {reporting ? "Cancel" : "Report a problem with this case"}
        </button>
        <button type="button" className="quiet" onClick={() => setWithdrawing((w) => !w)}>
          {withdrawing ? "Cancel" : "How we may contact you"}
        </button>
      </div>

      {reporting ? <ReportForm caseId={caseRow.id} onDone={() => setReporting(false)} /> : null}

      {/*
        Behind a disclosure, and per case, because that is the scope the consent
        rows actually have — `recordConsents` writes one row per case, and
        `hasConsentForCase` requires a case id precisely so a permission
        withdrawn on one case cannot be revived by another. A control that sat
        outside a case would imply a per-account setting the data model does not
        have. The account-wide version is a separate, differently-worded control
        at the foot of this page.
      */}
      {/*
        Both directions now, where there was only withdrawal. The previous
        control could turn a channel OFF and had no counterpart, so a client who
        declined at submission — or whose case predates the contact fieldset —
        had no way to say yes. It also could not show state; this one asks the
        server and says what is actually true.
      */}
      {withdrawing ? <CaseContactChannels caseId={caseRow.id} /> : null}
    </li>
  );
}

/**
 * Filing a report. Small on purpose: a category and a box.
 *
 * THE FORM CANNOT NAME A PROFESSIONAL, and that is structural rather than a
 * policy this page enforces. The API has no field for it — the subject is worked
 * out on the server from the case's own assignment history — so there is nowhere
 * for a client to write a name and nothing for a handler to have to strip.
 *
 * WHY THE FORM IS OFFERED ON EVERY CASE. A client is most likely to report about
 * an attorney at the moment that attorney has just come off their case, and at
 * that moment the case shows nobody assigned. Hiding the button on those cases
 * would hide it from precisely the people who need it most. The case payload
 * does not say whether anybody was EVER assigned, so the server is the judge:
 * where nobody ever was, it refuses with a clear reason and that reason is shown
 * as it came. Guessing here and hiding the control would be the worse error.
 */
function ReportForm({ caseId, onDone }: { caseId: string; onDone: () => void }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<ClientReportCategory>("WORK_NOT_COMPLETED");
  const [detail, setDetail] = useState("");

  const file = useMutation({
    mutationFn: async () =>
      apiFetch<MyClientReport>(`/cases/${caseId}/reports`, {
        token: await getToken(),
        method: "POST",
        body: { category, detail: detail.trim() || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports", "mine"] });
      onDone();
    },
  });

  return (
    // .consequence: what filing a report does, and what it does not do, said
    // before it is filed. The brass rule marks it as the platform's own voice.
    <div className="consequence">
      <h3>Tell us what has gone wrong</h3>
      <p>
        This goes to a person at TitleVerify, not to the attorney. Somebody will read it, and
        these are handled by talking to you rather than by messages inside this app.
      </p>
      {/*
        THE COPY HERE USED TO SAY "make sure the number on your account is one
        you answer". THERE IS NO NUMBER ON THE ACCOUNT.

        Verified rather than assumed: App.tsx posts only { accountType } to
        POST /accounts, so users.phone, users.email and users.full_name are null
        for every account this application creates, and nothing in apps/api/src
        ever updates them. There is no screen in the product that collects a
        telephone number. The old sentence therefore told a client to go and
        check something that does not exist, and implied a call-back the platform
        has no means to make — a comforting statement that happened to be false,
        which is the exact failure this product is built to avoid.

        It is replaced with the true position AND the consequence, because
        "we cannot call you" is useless to a client without "so here is what to
        do". Directing them to a channel we can verify is the honest ask.

        NOT FIXED HERE, AND OWED: an operator reading /admin/reports gets
        reportedByUserId and nothing else — no name, no email, no number — so
        there is currently no way for ops to reach a client who files a report.
        The fix is a server change (a contact field on the account, or surfacing
        the identity provider's email to ops) and is out of scope for this pass.
      */}
      <div className="caveat">
        <p>
          <strong>TitleVerify holds no telephone number for you.</strong> There is nowhere in
          this application to give one, and none was collected when your account was created. If
          you want to be reached some other way, the box below is the only place to say so.
        </p>
        <p>
          {/*
            Offering the box as a contact channel means telling the client what
            that box IS. `client_reports.detail` is free text on an erasable row
            with no retention clock over it — the schema declares that gap at
            TODO-BLOCKED-REPORT-RETENTION rather than shipping a sweep nothing
            calls. Inviting someone to put their phone number there without
            saying how long it stays would be trading one dishonesty for another.
          */}
          Before you do: what you write in that box has{" "}
          <strong>no deletion date on it yet</strong> — the clock that would remove it has not
          been built, so treat anything you type there as kept until a person removes it. That is
          your call to make, which is why we are telling you rather than deciding for you.
        </p>
      </div>
      <p>
        <small>
          What you write is recorded as <em>your</em> account of what happened. We do not decide
          whether an attorney has done anything wrong — that is a matter for the Bar Association
          of Sri Lanka and the Supreme Court, not for us. What we can decide is whether to keep
          sending cases to someone through this platform.
        </small>
      </p>

      <div className="field">
        <label htmlFor={`category-${caseId}`}>What has happened?</label>
        <select
          id={`category-${caseId}`}
          value={category}
          onChange={(e) => setCategory(e.target.value as ClientReportCategory)}
        >
          {CLIENT_REPORT_CATEGORIES.map((code) => (
            <option key={code} value={code}>
              {CATEGORY_LABEL[code]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor={`detail-${caseId}`}>Anything you want to add (optional)</label>
        <textarea
          id={`detail-${caseId}`}
          rows={4}
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder="In your own words — what you expected, and what happened instead."
        />
      </div>

      {file.error ? (
        <p role="alert">
          {/*
            The server's reason, still shown as it came — apiErrorText renders a
            `serverExplained` message verbatim, and the commonest refusal here is
            exactly that: "no professional has been assigned to this case yet, so
            there is no one to report about". That is a genuine answer to a
            genuine question and it must not be softened.

            What routing through apiErrorText changes is the OTHER refusals,
            which the old branch printed raw. A 403 on this route carries no
            reason at all — http/errors.ts strips it deliberately, so
            `ApiError.message` is the bare token "forbidden" — and a 5xx is
            "internal_error". Both were being shown to a client as though they
            were an account of what happened.
          */}
          {apiErrorText(file.error, "Could not send the report.")}
        </p>
      ) : null}

      <div className="actions">
        <button type="button" disabled={file.isPending} onClick={() => file.mutate()}>
          {file.isPending ? "Sending…" : "Send this to TitleVerify"}
        </button>
      </div>
    </div>
  );
}
